import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { createDevice, updateDevice } from "./api";

export type PadColorKeys = 'red' | 'pink' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple';

export interface Device {
  id: string;
  locationId: string;
  locationCode: string;
  locationColor: PadColorKeys;
}

interface DeviceFormModalProps {
  show: boolean;
  onHide: () => void;
  device?: Device; // If defined, we're editing
}

const DeviceFormModal: React.FC<DeviceFormModalProps> = ({ show, onHide, device }) => {
  const [form, setForm] = useState({
    locationId: "",
    locationCode: "",
    locationColor: "blue" as PadColorKeys,
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (device) {
      setForm({
        locationId: device.locationId,
        locationCode: device.locationCode,
        locationColor: device.locationColor,
      });
    } else {
      setForm({
        locationId: "",
        locationCode: "",
        locationColor: "blue",
      });
    }
    setErrors([]);
  }, [device, show]);

  const validate = () => {
    const missing = Object.entries(form)
      .filter(([, v]) => !v.trim())
      .map(([k]) => k);
    setErrors(missing);
    return missing.length === 0;
  };

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    try {
      if (device) {
        await updateDevice(device.id, form);
        toast.success("Device updated");
      } else {
        await createDevice(form);
        toast.success("Device created");
      }
      onHide();
    } catch (err) {
      toast.error("Error saving device");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{device ? "Update Device" : "Create Device"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Location ID</Form.Label>
            <Form.Control
              value={form.locationId}
              onChange={(e) => handleChange("locationId", e.target.value)}
              isInvalid={errors.includes("locationId")}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Location Code</Form.Label>
            <Form.Control
              value={form.locationCode}
              onChange={(e) => handleChange("locationCode", e.target.value.toUpperCase())}
              maxLength={4}
              isInvalid={errors.includes("locationCode")}
            />
            <Form.Text className="text-muted">3–4 uppercase letters</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Location Color</Form.Label>
            <Form.Select
              value={form.locationColor}
              onChange={(e) => handleChange("locationColor", e.target.value)}
              isInvalid={errors.includes("locationColor")}
            >
              {["red", "pink", "orange", "yellow", "green", "blue", "purple"].map((color) => (
                <option key={color} value={color}>
                  {color.charAt(0).toUpperCase() + color.slice(1)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : device ? "Update" : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeviceFormModal;